import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart, ZAR } from "@/lib/cart";
import { mediaUrl } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import FreeShipProgress from "@/components/FreeShipProgress";
import { CART } from "@/constants/testIds";
import { Trash, Plus, Minus, ArrowRight } from "@phosphor-icons/react";

export default function Cart() {
  const { items, remove, updateQty, subtotal } = useCart();
  const nav = useNavigate();
  const { user } = useAuth();
  const [threshold, setThreshold] = React.useState(500);
  React.useEffect(() => {
    api.get("/content").then((r) => setThreshold(r.data.free_shipping_threshold || 500));
  }, []);

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-[900px] px-4 py-24 text-center sm:px-6 sm:py-40 lg:px-10">
        <div className="font-label text-xs text-[#FF5A1F]">/ CART</div>
        <h1 data-testid={CART.emptyMsg} className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-5xl">
          Your cart is <span className="text-white/30">empty</span>.
        </h1>
        <Link
          to="/shop"
          className="mt-8 sm:mt-10 inline-flex items-center gap-2 bg-[#FF5A1F] px-6 sm:px-8 py-3 sm:py-4 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
        >
          Start shopping <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 sm:py-16 lg:px-10">
      <div className="font-label text-xs text-[#00FFC2]">/ CART</div>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-5xl">Your bag</h1>

      <div className="mt-8 sm:mt-12 grid gap-8 lg:gap-12 lg:grid-cols-3">
        <div data-testid={CART.itemsList} className="lg:col-span-2 space-y-3 sm:space-y-4">
          <FreeShipProgress subtotal={subtotal} threshold={threshold} />
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-3 sm:gap-4 border border-white/10 bg-[#0d0d0d] p-3 sm:p-4">
              <div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 overflow-hidden border border-white/10 bg-black">
                {it.image ? (
                  <img src={mediaUrl(it.image)} alt="" className="h-full w-full object-cover" />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm font-bold truncate">{it.name}</div>
                {it.variant && (
                  <div className="mt-1 text-[11px] text-white/50 line-clamp-2">
                    {Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  </div>
                )}
                <div className="mt-2 text-sm text-[#00FFC2]">{ZAR(it.unit_price)}</div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button
                  data-testid={CART.removeBtn}
                  onClick={() => remove(idx)}
                  className="text-white/40 hover:text-[#FF5A1F] transition-colors"
                >
                  <Trash size={16} />
                </button>
                <div className="flex items-center border border-white/15">
                  <button onClick={() => updateQty(idx, it.quantity - 1)} className="px-2 py-1">
                    <Minus size={12} />
                  </button>
                  <input
                    data-testid={CART.qtyInput}
                    type="number"
                    value={it.quantity}
                    onChange={(e) => updateQty(idx, Number(e.target.value) || 1)}
                    className="w-9 bg-transparent text-center text-sm outline-none"
                  />
                  <button onClick={() => updateQty(idx, it.quantity + 1)} className="px-2 py-1">
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="border border-white/10 bg-[#0d0d0d] p-5 sm:p-6 h-fit lg:sticky lg:top-24">
          <div className="font-label text-xs text-white/50">SUMMARY</div>
          <div className="mt-5 sm:mt-6 flex items-baseline justify-between">
            <span className="text-white/70">Subtotal</span>
            <span data-testid={CART.subtotal} className="font-display text-2xl font-black text-[#00FFC2]">
              {ZAR(subtotal)}
            </span>
          </div>
          <div className="mt-1 text-xs text-white/40">Shipping & discounts applied at checkout.</div>
          <button
            data-testid={CART.checkoutBtn}
            onClick={() => nav("/checkout")}
            className="mt-6 sm:mt-8 w-full bg-[#FF5A1F] py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
          >
            Proceed to checkout
          </button>
          <Link
            to="/shop"
            className="mt-3 block text-center text-xs font-label text-white/50 hover:text-white transition-colors"
          >
            Continue shopping
          </Link>
        </aside>
      </div>
    </div>
  );
}
